import React, { ReactElement } from 'react';
import useEndOfYearProcessing from '../hooks/use-end-of-year-processing';
import Typography from '@material-ui/core/Typography';
import useUiStore from 'store/ui';
import EndOfYearProcessingInfo from './components/EndOfYearProcessingInfo';
import EndOfYearProcessingForm from './components/EndOfYearProcessingForm';
import { useTranslation } from 'react-i18next';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { Theme } from 'types';
import { Button } from '@material-ui/core';
import { EndOfYearProcessingFormValues } from '../types';
import { validateForm } from '../utils';

const EndOfYearProcessingModal: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation('EndOfYearProcessing');
  const [, { closeModal }] = useUiStore();
  const { values, handleUpdate } = useEndOfYearProcessing();

  const handleSubmit = async (
    values: EndOfYearProcessingFormValues,
    { setSubmitting }: FormikHelpers<EndOfYearProcessingFormValues>
  ): Promise<void> => {
    await handleUpdate(values);
    setSubmitting(false);
  };

  return (
    <Formik
      enableReinitialize
      initialValues={values}
      validateOnChange={true}
      validate={formValues => validateForm(formValues, values, t)}
      onSubmit={handleSubmit}
    >
      {({
        isSubmitting,
        isValid,
        submitForm
      }: FormikProps<EndOfYearProcessingFormValues>): ReactElement => (
        <div className={classes.root}>
          <div className={classes.header}>
            <Typography variant="h2" component="h1">
              {t('End of Year Processing')}
            </Typography>
          </div>
          <div className={classes.body}>
            <EndOfYearProcessingInfo />
            <EndOfYearProcessingForm />
          </div>
          <div className={classes.footer}>
            <Button
              aria-label={t('Cancel')}
              className={classes.cancelButton}
              color="secondary"
              variant="outlined"
              disableElevation
              onClick={() => closeModal()}
            >
              {t('Cancel')}
            </Button>
            <Button
              aria-label={t('Save Changes')}
              className={classes.saveButton}
              variant="contained"
              color="primary"
              disableElevation
              onClick={submitForm}
              disabled={isSubmitting || !isValid}
            >
              {t('Save Changes')}
            </Button>
          </div>
        </div>
      )}
    </Formik>
  );
};

const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    root: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    },
    body: {
      height: `calc(100% - ${theme.constants.modals.footerHeight}px)`,
      overflow: 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      padding: theme.spacing(3)
    },
    header: {
      justifyContent: 'flex-start',
      alignItems: 'center',
      display: 'flex',
      minHeight: 100,
      paddingLeft: theme.spacing(4),
      borderBottom: `1px solid ${theme.palette.divider}`,
      '& > h4': {
        fontWeight: 600
      }
    },
    footer: {
      width: '100%',
      justifyContent: 'flex-end',
      alignItems: 'center',
      display: 'flex',
      minHeight: theme.constants.modals.footerHeight,
      borderTop: `1px solid ${theme.palette.divider}`
    },
    saveButton: {
      marginRight: theme.spacing(6)
    },
    cancelButton: {
      marginRight: theme.spacing(3)
    }
  });
});

export default EndOfYearProcessingModal;
